import type { Tagged } from "@-/com.npmjs.type-fest";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { createGate } from "@-/dev.improve.haniverse.lib.gate";
import { LearnLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import { useEffect, useState } from "react";

const CELEBRATION_DURATION_MS = 3200;

export type ReviewCompletionCelebrationGate = Tagged<
  {
    /** The `reviewCelebration` tick being shown; a new tick restarts the confetti. */
    celebration: number;
  },
  "ReviewCompletionCelebrationGate"
>;

/**
 * Open for a few seconds after each `reviewCelebration` tick. `null` is the designed hidden state
 * (no celebration yet, or the last one has finished). The timer lives here, in the parent, so the
 * celebration's lifetime doesn't depend on the overlay being mounted.
 */
export function useReviewCompletionCelebrationGate(): ReviewCompletionCelebrationGate | null {
  const [celebration] = useContextSelectors(LearnLayoutContext, (c) => c.reviewCelebration);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (celebration === 0) {
      return;
    }
    setVisible(true);
    const timeout = setTimeout(setVisible, CELEBRATION_DURATION_MS, false);
    return () => clearTimeout(timeout);
  }, [celebration]);
  return visible ? createGate<ReviewCompletionCelebrationGate>({ celebration }) : null;
}
